import * as React from 'react'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { useParams } from 'react-router-dom'
import { PrintoutConfigurationModel } from '@/lib/api/company/configuration/printout/types'
import { PrintoutForm } from './PrintoutForm'
import { PrintoutSkeleton } from './PrintoutSkeleton'
import { useQueryPrintoutConfiguration } from '@/lib/api/company/configuration/printout/useQueryPrintoutConfiguration'
import { useMutationPrintoutConfiguration } from '@/lib/api/company/configuration/printout/useMutationPrintoutConfiguration'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

// GPS data configuration page
export const PrintoutPageContent = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t } = useLocalize()
  const query = useQueryPrintoutConfiguration({ companyId })
  const mutation = useMutationPrintoutConfiguration(companyId)
  const lastQueryData =
    React.useRef<PrintoutConfigurationModel | null>(null)

  const [state, setState] =
    React.useState<PrintoutConfigurationModel | null>(null)

  React.useEffect(() => {
    const data = query.data
    if (!data) return
    setState((prev) => ({
      ...prev,
      ...data,
    }))
    lastQueryData.current = data
  }, [query.data])

  const isDirty = React.useMemo(() => {
    return JSON.stringify(lastQueryData.current) !== JSON.stringify(state)
  }, [state, lastQueryData])

  const handleReset = () => {
    setState((prev) => ({
      ...prev,
      ...lastQueryData.current,
    }))
  }

  const handleSave = () => {
    mutation.mutate(state)
  }

  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    printoutGenerationDate: t('configuration-printout.show-generation-date'),
    printoutCompanyName: t('configuration-printout.show-company-name'),
    printoutDriverName: t('configuration-printout.add-driver-name-signature'),
    printoutSingleGeneratedFileForEntity: t('configuration-printout.separate-pdf-files'),
    printTripVertically: t('configuration-printout.print-trip-vertically'),
    evenPagesTimesheetPrint: t('configuration-printout.even-pages'),
    extendPrintMargins: t('configuration-printout.extended-margins'),
    groupPageNumbersByDrivers: t('configuration-printout.group-page-numbers'),
    driversCountForDocumentGeneration: t('configuration-printout.drivers-count-threshold'),
    printEmploymentConditionsInfo: t('configuration-printout.print-employment-conditions'),
    otherEmploymentConditionsText: t(
      'configuration-printout.other-employment-conditions-text',
    ),
    printWorkingTimeDeclaration: t('configuration-printout.print-working-time-declaration'),
    printEqualTreatmentDeclaration: t('configuration-printout.print-equal-treatment'),
    printRiskDeclaration: t('configuration-printout.print-risk-declaration'),
    printNoBanDeclaration: t('configuration-printout.print-no-ban-declaration'),
    printForeignerDeclaration: t('configuration-printout.print-foreigner-declaration'),
  }), [t])

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={query.isFetching || mutation.isPending}
      isError={query.isError}
      handleSave={handleSave}
      handleReset={handleReset}
      isDirty={isDirty}
      errorMsg={t('configuration-printout.load-error')}
      globalErrorMsg={t('configuration-printout.load-error-global')}
      diffBefore={lastQueryData.current as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
      diffLabels={diffLabels}
    >
      {state ? (
        <PrintoutForm
          isSaving={mutation.isPending}
          state={state}
          setState={setState}
        />
      ) : (
        <PrintoutSkeleton />
      )}
    </SettingsPageWrapper>
  )
}
